import { Geolocation } from '@capacitor/geolocation';

/**
 * Location Service - Manages GPS access and coordinate capture
 * Supports both mobile (Capacitor) and web (Navigator.geolocation) platforms
 */

const LOCATION_PERMISSIONS_KEY = 'rtb_location_permissions_granted';
const LOCATION_CACHE_KEY = 'rtb_last_location';
const LOCATION_CACHE_DURATION = 60000; // 1 minute - reuse recent location

/** Check if running on native mobile platform */
const isNativePlatform = () => {
  try {
    // Try to import and check Capacitor
    const { Capacitor } = require('@capacitor/core');
    return Capacitor && Capacitor.isNativePlatform && Capacitor.isNativePlatform();
  } catch (e) {
    // Capacitor not available - we're on web
    return false;
  }
};

/** Check if browser Geolocation API is available */
const isBrowserGeolocationAvailable = () => {
  return typeof navigator !== 'undefined' && 'geolocation' in navigator && navigator.geolocation !== undefined;
};

export const locationService = {
  /**
   * Request GPS permission from user
   * Works on both native and web platforms
   */
  async requestPermission() {
    try {
      // Try native platform first (mobile)
      if (isNativePlatform()) {
        const permission = await Geolocation.requestPermissions();
        
        if (permission.location === 'granted' || permission.location === 'prompt-only') {
          localStorage.setItem(LOCATION_PERMISSIONS_KEY, 'granted');
          return { success: true, permission: permission.location };
        } else if (permission.location === 'denied') {
          localStorage.removeItem(LOCATION_PERMISSIONS_KEY);
          return { success: false, permission: 'denied', error: 'Location access denied' };
        }
      }
      
      // Fallback to browser Geolocation API (web)
      if (isBrowserGeolocationAvailable()) {
        return new Promise((resolve) => {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              localStorage.setItem(LOCATION_PERMISSIONS_KEY, 'granted');
              resolve({ success: true, permission: 'granted', platform: 'web' });
            },
            (error) => {
              if (error.code === error.PERMISSION_DENIED) {
                localStorage.removeItem(LOCATION_PERMISSIONS_KEY);
                resolve({ success: false, permission: 'denied', error: 'Location access denied', platform: 'web' });
              } else {
                resolve({ success: true, permission: 'granted', platform: 'web' }); // Assume granted if other error
              }
            }
          );
        });
      }
      
      // No platform available
      return { success: false, error: 'Geolocation not available on this platform' };
    } catch (error) {
      console.error('Error requesting location permission:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Check if GPS permission is granted
   * Works on both native and web platforms
   * FIXED: No longer calls getCurrentPosition() on web without user gesture
   */
  async hasPermission() {
    try {
      // Check localStorage cache first
      const cached = localStorage.getItem(LOCATION_PERMISSIONS_KEY);
      if (cached === 'granted') {
        return true;
      }

      // Try native platform first (mobile)
      if (isNativePlatform()) {
        const permission = await Geolocation.checkPermissions();
        const hasAccess = permission.location === 'granted' || permission.location === 'prompt-only';
        
        if (hasAccess) {
          localStorage.setItem(LOCATION_PERMISSIONS_KEY, 'granted');
        } else {
          localStorage.removeItem(LOCATION_PERMISSIONS_KEY);
        }
        
        return hasAccess;
      }
      
      // FIXED: For web browsers, don't call getCurrentPosition() without user gesture
      // Just return false and let the banner prompt the user
      // This prevents geolocation permission denial from blocking page transitions
      if (isBrowserGeolocationAvailable()) {
        return false; // No cached permission, wait for user gesture
      }
      
      return false;
    } catch (error) {
      console.error('Error checking location permission:', error);
      return false;
    }
  },

  /**
   * Get current GPS coordinates (high precision)
   * Works on both native and web platforms
   * @returns {Object} { latitude, longitude, accuracy, timestamp }
   */
  async getCurrentLocation() {
    try {
      const hasPermission = await this.hasPermission();
      if (!hasPermission) {
        return { success: false, error: 'Location permission not granted' };
      }

      // Check cache first
      const cached = this.getCachedLocation();
      if (cached) {
        return { success: true, location: cached, fromCache: true };
      }

      // Try native platform first (mobile)
      if (isNativePlatform()) {
        try {
          const position = await Geolocation.getCurrentPosition({
            enableHighAccuracy: true,
            timeout: 30000, // 30 second timeout
            maximumAge: 0, // Don't use cached system location
          });

          const location = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy, // meters
            altitude: position.coords.altitude,
            altitudeAccuracy: position.coords.altitudeAccuracy,
            timestamp: new Date().toISOString(),
          };

          // Cache the location
          this.setCachedLocation(location);

          return { success: true, location, fromCache: false, platform: 'native' };
        } catch (nativeError) {
          console.warn('Native Geolocation failed, falling back to browser API:', nativeError);
          // Fall through to browser API
        }
      }

      // Fallback to browser Geolocation API (web)
      if (isBrowserGeolocationAvailable()) {
        return new Promise((resolve) => {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              const location = {
                latitude: position.coords.latitude,
                longitude: position.coords.longitude,
                accuracy: position.coords.accuracy, // meters
                altitude: position.coords.altitude,
                altitudeAccuracy: position.coords.altitudeAccuracy,
                timestamp: new Date().toISOString(),
              };

              // Cache the location
              this.setCachedLocation(location);

              resolve({ success: true, location, fromCache: false, platform: 'web' });
            },
            (error) => {
              console.error('Browser Geolocation error:', error);
              resolve({
                success: false,
                error: error.message || 'Failed to get location',
                code: error.code,
                platform: 'web',
              });
            },
            {
              enableHighAccuracy: true,
              timeout: 30000,
              maximumAge: 0,
            }
          );
        });
      }

      return { success: false, error: 'Geolocation not available on this platform' };
    } catch (error) {
      console.error('Error getting current location:', error);
      return {
        success: false,
        error: error.message || 'Failed to get location',
        code: error.code,
      };
    }
  },

  /**
   * Check if location is within geofence of venue
   * @param {Object} venueLat, venueLng - venue coordinates
   * @param {Number} radiusMeters - allowed radius (default 100m)
   */
  async verifyLocationAtVenue(venueLat, venueLng, radiusMeters = 100) {
    const result = await this.getCurrentLocation();
    
    if (!result.success) {
      return { verified: false, error: result.error };
    }

    const { latitude, longitude } = result.location;
    const distance = this.calculateDistance(latitude, longitude, venueLat, venueLng);

    return {
      verified: distance <= radiusMeters,
      distance,
      radiusMeters,
      withinBounds: distance <= radiusMeters,
    };
  },

  /**
   * Calculate distance between two coordinates (Haversine formula)
   * Returns distance in meters
   */
  calculateDistance(lat1, lon1, lat2, lon2) {
    const R = 6371000; // Earth radius in meters
    const φ1 = (lat1 * Math.PI) / 180;
    const φ2 = (lat2 * Math.PI) / 180;
    const Δφ = ((lat2 - lat1) * Math.PI) / 180;
    const Δλ = ((lon2 - lon1) * Math.PI) / 180;

    const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
              Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c; // Distance in meters
  },

  /**
   * Cache location locally
   */
  setCachedLocation(location) {
    const cacheData = {
      ...location,
      cachedAt: Date.now(),
    };
    localStorage.setItem(LOCATION_CACHE_KEY, JSON.stringify(cacheData));
  },

  /**
   * Get cached location if still valid
   */
  getCachedLocation() {
    const cached = localStorage.getItem(LOCATION_CACHE_KEY);
    if (!cached) return null;

    try {
      const data = JSON.parse(cached);
      const age = Date.now() - data.cachedAt;

      if (age < LOCATION_CACHE_DURATION) {
        return data;
      }
    } catch (e) {
      console.error('Error parsing cached location:', e);
    }

    localStorage.removeItem(LOCATION_CACHE_KEY);
    return null;
  },

  /**
   * Clear cached location
   */
  clearCache() {
    localStorage.removeItem(LOCATION_CACHE_KEY);
  },

  /**
   * Format location for submission metadata
   */
  formatLocationMetadata(location) {
    return {
      gps_latitude: location.latitude,
      gps_longitude: location.longitude,
      gps_accuracy: `${Math.round(location.accuracy)}m`,
      gps_timestamp: location.timestamp,
      gps_altitude: location.altitude,
    };
  },
};

export default locationService;
